import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, apiDownload, ApiError, apiUpload } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Badge, ErrorBox } from '../components/common';
import { Icon } from '../components/icons';
import { formatDate } from '../labels';
import { discard, pending, type SyncOutcome, synchronize } from '../offline/queue';
import { enqueue } from '../offline/queue';
import { DeliveryCard } from './Delivery';
import { NipCard } from './Nip';
import { PaymentCard } from './Finance';
import { EMPTY_IDENTITY, IdentityFields, type IdentityForm, identityPayload } from './Registry';
import '../styles/demandes.css';

// ---------- Types ----------

interface AppType {
  code: string;
  label: string;
  description: string;
  requester_kind: string;
  payment_required: boolean;
  is_fictitious: boolean;
  documents: { code: string; mandatory: boolean }[];
}
interface RequestRefs {
  types: AppType[];
  documentTypes: { code: string; label: string; help: string; max_bytes: number }[];
  refusalReasons: { code: string; label: string; public_text: string }[];
  statusLabels: Record<string, string>;
}
interface AppSummary {
  id: string;
  reference: string;
  type_label: string;
  channel: string;
  status: string;
  statusLabel: string;
  declared_identity: { surname?: string; givenNames?: string };
  submitted_at: string | null;
  updated_at: string;
}
interface AppDetail {
  id: string;
  reference: string;
  type_code: string;
  type_label: string;
  channel: 'en_ligne' | 'guichet';
  counter_name: string | null;
  declared_identity: Record<string, string | number | undefined>;
  contact: { email?: string; phone?: string };
  status: string;
  statusLabel: string;
  internal_status?: string | null;
  oriented_jurisdiction: string | null;
  refusal_text: string | null;
  submitted_at: string | null;
  created_at: string;
  role: 'owner' | 'counter' | 'instructor';
  documents: {
    id: string;
    document_code: string;
    label: string;
    filename: string;
    mime: string;
    size_bytes: number;
    uploaded_at: string;
  }[];
  missingDocuments: { code: string; label: string }[];
  complements: {
    message: string;
    document_codes: string[];
    requested_at: string;
    answered_at: string | null;
  }[];
  history: {
    status: string;
    label: string;
    publicNote: string | null;
    occurredAt: string;
    internal?: string | null;
    reason?: string | null;
  }[];
  trackingCode?: string;
}

export const STATUS_TONE: Record<string, string> = {
  brouillon: 'muted',
  deposee: 'info',
  complement_demande: 'warn',
  paiement_attendu: 'warn',
  traitement_en_cours: 'info',
  verification_complementaire: 'info',
  document_disponible: 'ok',
  refusee: 'danger',
  cloturee: 'muted',
};

/** Statut public d'une demande, en puce (maquette « Sable & okoumé »). */
function StatusChip({ status, label }: { status: string; label: string }) {
  const tone = STATUS_TONE[status] ?? 'muted';
  return <span className={tone === 'muted' ? 'chip' : `chip ${tone}`}>{label}</span>;
}

function useRequestRefs(): RequestRefs | null {
  const [r, setR] = useState<RequestRefs | null>(null);
  useEffect(() => {
    api<RequestRefs>('/demandes/referentiels')
      .then(setR)
      .catch(() => setR(null));
  }, []);
  return r;
}

function can(me: { permissions: string[] } | null, p: string) {
  return me?.permissions.includes(p) ?? false;
}

// ---------- Listes ----------

export function ApplicationsListPage({ mode }: { mode: 'usager' | 'guichet' | 'instruction' }) {
  const { me } = useAuth();
  const [params, setParams] = useSearchParams();
  const status = params.get('statut') ?? (mode === 'instruction' ? 'deposee' : '');
  const [rows, setRows] = useState<AppSummary[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  useEffect(() => {
    setRows(null);
    api<AppSummary[]>(`/demandes${status ? `?statut=${status}` : ''}`)
      .then(setRows)
      .catch(setError);
  }, [status]);

  const title =
    mode === 'usager' ? 'Mes demandes' : mode === 'guichet' ? 'Guichet' : 'Délivrance - demandes à instruire';
  const filters =
    mode === 'instruction'
      ? [
          ['deposee', 'Déposées'],
          ['complement_demande', 'Complément demandé'],
          ['paiement_attendu', 'Paiement attendu'],
          ['traitement_en_cours', 'En traitement'],
          ['refusee', 'Refusées'],
          ['', 'Toutes'],
        ]
      : [
          ['', 'Toutes'],
          ['brouillon', 'Brouillons'],
          ['deposee', 'Déposées'],
          ['complement_demande', 'Complément demandé'],
        ];

  return (
    <div className="stack dm">
      <div className="head">
        <div>
          <h1>{title}</h1>
          {mode === 'instruction' && (
            <p className="lead">
              Recevabilité, compléments et refus motivés. La préparation, la signature et la remise des
              bulletins arrivent avec le moteur de règles (Lot 6).
            </p>
          )}
        </div>
        {mode === 'usager' && can(me, 'demande.gerer_propres') && (
          <div className="actions">
            <Link className="btn" to="/espace/demandes/nouvelle">
              Nouvelle demande
            </Link>
            <Link className="btn btn-ghost" to="/espace/demandes/signalements">
              Signaler une erreur
            </Link>
          </div>
        )}
        {mode === 'guichet' && (
          <div className="actions">
            <Link className="btn btn-gold" to="/espace/guichet/nouvelle">
              <Icon name="desk" />
              Accueillir un usager
            </Link>
          </div>
        )}
      </div>
      {mode === 'usager' && <ContactsCard />}
      {mode === 'guichet' && <OfflineQueueCard />}
      <div className="tabs" role="group" aria-label="Filtrer par statut">
        {filters.map(([v, l]) => (
          <button
            key={v}
            type="button"
            className="tab"
            aria-pressed={status === v}
            onClick={() => setParams({ statut: v! })}
          >
            {l}
          </button>
        ))}
      </div>
      <ErrorBox error={error} />
      {rows !== null && rows.length === 0 ? (
        <section className="card np">
          <div className="empty">
            <span className="ic">
              <Icon name="file" />
            </span>
            <span>Aucune demande.</span>
          </div>
        </section>
      ) : (
        <section className="card np">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th scope="col">Référence</th>
                  <th scope="col">Demande</th>
                  {mode !== 'usager' && <th scope="col">Personne</th>}
                  <th scope="col">Statut</th>
                  <th scope="col">Mise à jour</th>
                </tr>
              </thead>
              <tbody>
                {rows === null ? (
                  <tr>
                    <td colSpan={5}>Chargement…</td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <Link className="mono dm-ref" to={`/espace/demandes/${r.id}`}>
                          {r.reference}
                        </Link>
                      </td>
                      <td>
                        {r.type_label}
                        {r.channel === 'guichet' && <small className="dm-sub">Dépôt au guichet</small>}
                      </td>
                      {mode !== 'usager' && (
                        <td>
                          {r.declared_identity.surname} {r.declared_identity.givenNames}
                        </td>
                      )}
                      <td>
                        <StatusChip status={r.status} label={r.statusLabel} />
                      </td>
                      <td className="dm-date">{formatDate(r.updated_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

function ContactsCard() {
  const [contacts, setContacts] = useState<{ channel: string; value: string; verified_at: string | null }[]>(
    [],
  );
  const [form, setForm] = useState({ channel: 'email', value: '', code: '' });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const load = () =>
    api<typeof contacts>('/moi/contacts')
      .then(setContacts)
      .catch(() => setContacts([]));
  useEffect(() => {
    void load();
  }, []);
  const send = async () => {
    setError(null);
    try {
      await api('/moi/contacts/code', { method: 'POST', body: { channel: form.channel, value: form.value } });
      setSent(true);
    } catch (e) {
      setError(e as ApiError);
    }
  };
  const confirm = async () => {
    setError(null);
    try {
      await api('/moi/contacts/confirmer', {
        method: 'POST',
        body: { channel: form.channel, code: form.code },
      });
      setSent(false);
      setForm({ ...form, code: '' });
      await load();
    } catch (e) {
      setError(e as ApiError);
    }
  };
  return (
    <details className="acc">
      <summary>
        <span>
          Mes moyens de contact
          <small>Courriel et téléphone pour les notifications d’étape</small>
        </span>
        {contacts.some((c) => c.verified_at) && (
          <span className="dm-verified">
            {contacts
              .filter((c) => c.verified_at)
              .map((c) => (
                <span key={c.channel} className="chip ok">
                  {c.channel === 'email' ? 'Courriel vérifié' : 'Téléphone vérifié'}
                </span>
              ))}
          </span>
        )}
      </summary>
      <div className="body dm-flow">
        <p className="small muted" style={{ margin: 0 }}>
          La vérification prouve que vous maîtrisez ce contact ; elle ne vaut pas vérification d’identité. Les
          messages annoncent une étape, jamais un contenu judiciaire.
        </p>
        <div className="fg">
          <div className="field">
            <label htmlFor="ct-channel">Canal</label>
            <select
              id="ct-channel"
              value={form.channel}
              onChange={(e) => setForm({ ...form, channel: e.target.value })}
            >
              <option value="email">Courriel</option>
              <option value="sms">SMS</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="ct-value">{form.channel === 'email' ? 'Adresse' : 'Numéro'}</label>
            <input
              id="ct-value"
              value={form.value}
              onChange={(e) => setForm({ ...form, value: e.target.value })}
            />
          </div>
          {sent && (
            <div className="field">
              <label htmlFor="ct-code">Code reçu</label>
              <input
                id="ct-code"
                inputMode="numeric"
                maxLength={6}
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
              />
            </div>
          )}
        </div>
        <ErrorBox error={error} />
        <div className="actions">
          <button type="button" className="btn btn-ghost" onClick={() => void send()} disabled={!form.value}>
            Recevoir un code
          </button>
          {sent && (
            <button
              type="button"
              className="btn"
              onClick={() => void confirm()}
              disabled={form.code.length !== 6}
            >
              Confirmer
            </button>
          )}
        </div>
      </div>
    </details>
  );
}

// ---------- Création ----------

export function ApplicationFormPage({ counter }: { counter?: boolean }) {
  const refs = useRequestRefs();
  const navigate = useNavigate();
  const [typeCode, setTypeCode] = useState('');
  const [identity, setIdentity] = useState<IdentityForm>(EMPTY_IDENTITY);
  const [contact, setContact] = useState({ email: '', phone: '' });
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  const [offline, setOffline] = useState(typeof navigator !== 'undefined' && !navigator.onLine);
  const [savedOffline, setSavedOffline] = useState(false);
  const [nipEnabled, setNipEnabled] = useState(false);
  const [withNip, setWithNip] = useState(false);
  useEffect(() => {
    void api<{ enabled: boolean }>('/verification-nip/etat')
      .then((s) => setNipEnabled(s.enabled))
      .catch(() => setNipEnabled(false));
  }, []);
  const types = (refs?.types ?? []).filter((t) => t.requester_kind === 'personne_concernee');
  useEffect(() => {
    if (!typeCode && types[0]) setTypeCode(types[0].code);
  }, [types, typeCode]);
  const type = types.find((t) => t.code === typeCode);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body = {
        typeCode,
        identity: identityPayload(identity),
        contact: {
          ...(contact.email ? { email: contact.email } : {}),
          ...(contact.phone ? { phone: contact.phone } : {}),
        },
        ...(counter ? { consent } : {}),
      };
      if (counter && offline) {
        // Saisie hors ligne : chiffrée sur le poste, transmise à la prochaine synchronisation (§18).
        await enqueue({
          type: 'brouillon_guichet.creer',
          payload: body,
          label: `${identity.surname} ${identity.givenNames}`.trim(),
        });
        setSavedOffline(true);
        setIdentity(EMPTY_IDENTITY);
        setConsent(false);
        return;
      }
      const created = await api<AppDetail>(counter ? '/demandes/guichet' : '/demandes', {
        method: 'POST',
        body,
      });
      navigate(`/espace/demandes/${created.id}`, {
        state: { trackingCode: created.trackingCode, nip: nipEnabled && withNip },
      });
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setBusy(false);
    }
  };

  const steps = ['Démarche', 'Identité', 'Contact', 'Pièces'];

  return (
    <div className="stack dm">
      <div className="head">
        <div>
          <p className="crumb">
            <Link to={counter ? '/espace/guichet' : '/espace/demandes'}>
              ‹ {counter ? 'Guichet' : 'Mes demandes'}
            </Link>
          </p>
          <h1>{counter ? 'Accueillir un usager au guichet' : 'Nouvelle demande'}</h1>
          {!counter && (
            <p className="lead">
              Votre brouillon est enregistré : vous pourrez ajouter les pièces et reprendre plus tard.
            </p>
          )}
        </div>
      </div>
      {counter && (
        <div className="alert alert-info small">
          Vous déposez pour le compte de l’usager présent. Ne lui demandez jamais son mot de passe. Faites
          vérifier la saisie par l’usager et remettez-lui le récépissé.
        </div>
      )}
      <ol className="dm-steps" aria-label="Étapes">
        {steps.map((label, i) => (
          <li key={label}>
            <span className="num">{i + 1}</span>
            {label}
          </li>
        ))}
      </ol>
      <form className="stack dm-form" onSubmit={submit}>
        <section className="card flow">
          <div className="sect">
            <span className="num" aria-hidden="true">
              1
            </span>
            <h2>Démarche</h2>
          </div>
          <div className="field">
            <label htmlFor="type">Type de demande</label>
            <select id="type" value={typeCode} onChange={(e) => setTypeCode(e.target.value)}>
              {types.map((t) => (
                <option key={t.code} value={t.code}>
                  {t.label}
                </option>
              ))}
            </select>
            {type && <span className="hint">{type.description}</span>}
          </div>
          {type && (
            <div className="dm-pieces">
              <span className="muted">Pièces à prévoir :</span>
              {type.documents.map((d) => (
                <span key={d.code} className="chip">
                  {`${refs?.documentTypes.find((x) => x.code === d.code)?.label ?? d.code}${d.mandatory ? '' : ' (facultatif)'}`}
                </span>
              ))}
            </div>
          )}
        </section>
        <section className="card flow">
          <div className="sect">
            <span className="num" aria-hidden="true">
              2
            </span>
            <h2>Identité de la personne concernée</h2>
          </div>
          <p className="small muted">
            Laissez vide ce que vous ne connaissez pas : rien ne sera complété à votre place.
          </p>
          {nipEnabled && !(counter && offline) && (
            <fieldset className="sub nip-choice">
              <legend>Vérification de l’identité (facultative)</legend>
              <label className="checkbox">
                <input type="radio" name="nip-choice" checked={withNip} onChange={() => setWithNip(true)} />
                <span>Vérifier mon identité avec mon NIP (juste après l’enregistrement du brouillon)</span>
              </label>
              <label className="checkbox">
                <input type="radio" name="nip-choice" checked={!withNip} onChange={() => setWithNip(false)} />
                <span>Continuer sans NIP : un agent vérifiera l’identité</span>
              </label>
            </fieldset>
          )}
          <div className="dm-fg">
            <IdentityFields value={identity} onChange={setIdentity} full />
          </div>
        </section>
        <section className="card flow">
          <div className="sect">
            <span className="num" aria-hidden="true">
              3
            </span>
            <h2>Contact</h2>
          </div>
          <div className="fg">
            <div className="field">
              <label htmlFor="c-email">Courriel</label>
              <input
                id="c-email"
                type="email"
                value={contact.email}
                onChange={(e) => setContact({ ...contact, email: e.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="c-phone">Téléphone</label>
              <input
                id="c-phone"
                inputMode="tel"
                value={contact.phone}
                onChange={(e) => setContact({ ...contact, phone: e.target.value })}
              />
            </div>
          </div>
        </section>
        {counter && (
          <section className="card flow">
            <label className="checkbox">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
              <span>L’usager a été informé du traitement de ses données et consent au dépôt assisté.</span>
            </label>
            <label className="checkbox">
              <input type="checkbox" checked={offline} onChange={(e) => setOffline(e.target.checked)} />
              <span>
                Saisie hors ligne (réseau indisponible) - la demande est chiffrée sur ce poste et transmise à
                la prochaine synchronisation ; le dépôt et le récépissé définitifs se font une fois en ligne.
              </span>
            </label>
          </section>
        )}
        {savedOffline && (
          <p className="alert alert-ok" role="status">
            Saisie enregistrée hors ligne. Synchronisez depuis l’espace Guichet dès que le réseau revient.
          </p>
        )}
        <ErrorBox error={error} />
        <div className="actions">
          <button className="btn" type="submit" disabled={busy || !typeCode || (counter && !consent)}>
            {busy
              ? 'Enregistrement…'
              : counter && offline
                ? 'Enregistrer hors ligne'
                : 'Enregistrer le brouillon'}
          </button>
          <Link className="btn btn-ghost" to={counter ? '/espace/guichet' : '/espace/demandes'}>
            Annuler
          </Link>
        </div>
      </form>
    </div>
  );
}

// ---------- Détail ----------

export function ApplicationDetailPage() {
  const { id } = useParams();
  const location = useLocation();
  const refs = useRequestRefs();
  const [d, setD] = useState<AppDetail | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [docCode, setDocCode] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [note, setNote] = useState('');
  const [refusal, setRefusal] = useState('');
  const trackingCode = (location.state as { trackingCode?: string } | null)?.trackingCode;
  const nipRequested = (location.state as { nip?: boolean } | null)?.nip;

  const load = useCallback(() => api<AppDetail>(`/demandes/${id}`).then(setD).catch(setError), [id]);
  useEffect(() => {
    void load();
  }, [load]);

  const act = async (path: string, body: unknown, done: string) => {
    setError(null);
    setNotice(null);
    try {
      setD(await api<AppDetail>(`/demandes/${id}${path}`, { method: 'POST', body }));
      setNotice(done);
      setNote('');
    } catch (e) {
      setError(e as ApiError);
    }
  };

  const sendFile = async () => {
    if (!file || !docCode) return;
    setError(null);
    const form = new FormData();
    form.append('documentCode', docCode);
    form.append('fichier', file);
    try {
      setD(await apiUpload<AppDetail>(`/demandes/${id}/pieces`, form));
      setFile(null);
      setNotice('Pièce transmise et contrôlée.');
    } catch (e) {
      setError(e as ApiError);
    }
  };

  const removeDoc = async (docId: string) => {
    try {
      setD(await api<AppDetail>(`/demandes/${id}/pieces/${docId}`, { method: 'DELETE' }));
    } catch (e) {
      setError(e as ApiError);
    }
  };

  if (!d) return error ? <ErrorBox error={error} /> : <p>Chargement…</p>;
  const type = refs?.types.find((t) => t.code === d.type_code);
  const editable =
    (d.role === 'owner' || d.role === 'counter') && ['brouillon', 'complement_demande'].includes(d.status);
  const ident = d.declared_identity;
  const birth = [ident.birthDay, ident.birthMonth, ident.birthYear].filter(Boolean).join('/');
  const pendingComplement = d.complements.find((c) => !c.answered_at);

  const instructing =
    d.role === 'instructor' &&
    [
      'deposee',
      'traitement_en_cours',
      'verification_complementaire',
      'paiement_attendu',
      'complement_demande',
    ].includes(d.status);

  return (
    <div className="stack dm">
      {trackingCode && <Receipt d={d} trackingCode={trackingCode} />}
      <div className="head">
        <div>
          {d.role === 'owner' && (
            <p className="crumb">
              <Link to="/espace/demandes">‹ Mes demandes</Link>
            </p>
          )}
          <h1 className="dm-title">
            Demande <span className="mono">{d.reference}</span>{' '}
            <StatusChip status={d.status} label={d.statusLabel} />
          </h1>
        </div>
      </div>
      <ErrorBox error={error} />
      {notice && (
        <p className="alert alert-ok" role="status">
          {notice}
        </p>
      )}
      {d.refusal_text && <p className="alert alert-danger">{d.refusal_text}</p>}
      {pendingComplement && (
        <div className="alert alert-warn">
          <span>
            <strong>Complément attendu :</strong> {pendingComplement.message}
          </span>
        </div>
      )}

      <div className="split">
        <div className="wide stack dm-col">
          <DeliveryCard applicationId={d.id} status={d.status} onChange={() => void load()} />
          <section className="card">
            <h2>Demande</h2>
            <dl className="details">
              <dt>Type</dt>
              <dd>{d.type_label}</dd>
              <dt>Canal</dt>
              <dd>
                {d.channel === 'guichet'
                  ? `Guichet${d.counter_name ? ` - ${d.counter_name}` : ''}`
                  : 'En ligne'}
              </dd>
              <dt>Personne</dt>
              <dd>
                {ident.surname} {ident.givenNames}
              </dd>
              <dt>Naissance</dt>
              <dd>
                {birth || 'non renseignée'}
                {ident.birthPlace ? `, ${ident.birthPlace}` : ''}
              </dd>
              {d.oriented_jurisdiction && (
                <>
                  <dt>Orientation</dt>
                  <dd>{d.oriented_jurisdiction}</dd>
                </>
              )}
              {d.internal_status && (
                <>
                  <dt>État interne</dt>
                  <dd>
                    {d.internal_status.charAt(0).toUpperCase() +
                      d.internal_status.slice(1).replace(/_/g, ' ').replace('attestee', 'attestée')}
                  </dd>
                </>
              )}
            </dl>
          </section>

          <NipCard
            applicationId={d.id}
            status={d.status}
            role={d.role}
            autoOpen={nipRequested}
            onChange={() => void load()}
          />

          <PaymentCard applicationId={d.id} status={d.status} role={d.role} onChange={() => void load()} />

          <section className="card flow">
            <h2>Pièces</h2>
            {d.documents.length === 0 ? (
              <p className="muted">Aucune pièce transmise.</p>
            ) : (
              <ul className="dm-docs">
                {d.documents.map((doc) => (
                  <li key={doc.id}>
                    <span className="dm-doc">
                      <Icon name="file" />
                      <span>
                        {doc.label}
                        <small className="dm-sub">
                          {doc.filename} · {Math.ceil(doc.size_bytes / 1024)} Ko
                        </small>
                      </span>
                    </span>
                    <span className="actions">
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => void apiDownload(`/demandes/${d.id}/pieces/${doc.id}`, doc.filename)}
                      >
                        Télécharger
                      </button>
                      {editable && (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => void removeDoc(doc.id)}
                        >
                          Retirer
                        </button>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {d.missingDocuments.length > 0 && (
              <p className="alert alert-warn small">
                Pièces obligatoires manquantes : {d.missingDocuments.map((m) => m.label).join(', ')}.
              </p>
            )}
            {editable && type && (
              <div className="fg">
                <div className="field">
                  <label htmlFor="doc-type">Pièce</label>
                  <select id="doc-type" value={docCode} onChange={(e) => setDocCode(e.target.value)}>
                    <option value="">- Choisir -</option>
                    {type.documents.map((x) => (
                      <option key={x.code} value={x.code}>
                        {refs?.documentTypes.find((t) => t.code === x.code)?.label ?? x.code}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="doc-file">Fichier (PDF, JPEG ou PNG)</label>
                  <input
                    id="doc-file"
                    type="file"
                    accept="application/pdf,image/jpeg,image/png"
                    onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  />
                </div>
                <div className="field dm-end">
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={!file || !docCode}
                    onClick={() => void sendFile()}
                  >
                    Transmettre
                  </button>
                </div>
              </div>
            )}
          </section>

          {editable && (
            <section className="card flow">
              <h2>
                {d.status === 'complement_demande' ? 'Transmettre le complément' : 'Déposer la demande'}
              </h2>
              <p className="small muted">
                Vérifiez le récapitulatif ci-dessus. Le dépôt contrôle que les informations et pièces
                obligatoires sont présentes.
              </p>
              <div className="actions">
                <button
                  type="button"
                  className="btn"
                  onClick={() => void act('/deposer', undefined, 'Demande déposée.')}
                >
                  {d.status === 'complement_demande' ? 'Transmettre' : 'Déposer'}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => void act('/annuler', undefined, 'Demande annulée.')}
                >
                  Annuler la demande
                </button>
              </div>
            </section>
          )}

          {instructing && (
            <section className="card flow">
              <h2>Instruction</h2>
              <div className="field">
                <label htmlFor="instr-note">
                  Note ou message (le message de complément est communiqué à l’usager)
                </label>
                <textarea id="instr-note" value={note} onChange={(e) => setNote(e.target.value)} />
              </div>
              <div className="actions">
                {d.status === 'deposee' && (
                  <button
                    type="button"
                    className="btn"
                    disabled={note.trim().length < 5}
                    onClick={() => void act('/recevable', { note }, 'Demande déclarée recevable.')}
                  >
                    Déclarer recevable
                  </button>
                )}
                {['deposee', 'traitement_en_cours', 'verification_complementaire'].includes(d.status) && (
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={note.trim().length < 10}
                    onClick={() => void act('/complement', { message: note }, 'Complément demandé.')}
                  >
                    Demander un complément
                  </button>
                )}
              </div>
              <div className="fg">
                <div className="field">
                  <label htmlFor="refusal">Motif de refus autorisé</label>
                  <select id="refusal" value={refusal} onChange={(e) => setRefusal(e.target.value)}>
                    <option value="">- Choisir -</option>
                    {(refs?.refusalReasons ?? []).map((r) => (
                      <option key={r.code} value={r.code}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                  {refusal && (
                    <span className="hint">
                      Texte communiqué : {refs?.refusalReasons.find((r) => r.code === refusal)?.public_text}
                    </span>
                  )}
                </div>
                <div className="field dm-end">
                  <button
                    type="button"
                    className="btn btn-danger"
                    disabled={!refusal || note.trim().length < 5}
                    onClick={() =>
                      void act('/refuser', { reasonCode: refusal, internalNote: note }, 'Demande refusée.')
                    }
                  >
                    Refuser (la note reste interne)
                  </button>
                </div>
              </div>
            </section>
          )}
        </div>

        <section className="card narrow">
          <h2>Suivi</h2>
          <ol className="tl">
            {d.history.map((h, i) => (
              <li key={i} className={i === d.history.length - 1 ? 'now' : 'done'}>
                <b>{h.label}</b>
                <span>{formatDate(h.occurredAt)}</span>
                {h.publicNote && <p className="dm-note">{h.publicNote}</p>}
                {h.reason && <p className="dm-note muted">Interne : {h.reason}</p>}
              </li>
            ))}
          </ol>
        </section>
      </div>
    </div>
  );
}

function Receipt({ d, trackingCode }: { d: AppDetail; trackingCode: string }) {
  return (
    <section className="card flow dm-receipt">
      <div className="ch">
        <h2>Récépissé</h2>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => window.print()}>
          Imprimer
        </button>
      </div>
      <dl className="details">
        <dt>Référence</dt>
        <dd>
          <span className="mono">{d.reference}</span>
        </dd>
        <dt>Code de suivi</dt>
        <dd>
          <span className="mono dm-code">{trackingCode}</span>
        </dd>
        <dt>Date</dt>
        <dd>{formatDate(d.created_at)}</dd>
        <dt>Démarche</dt>
        <dd>{d.type_label}</dd>
      </dl>
      <p className="small muted">
        Conservez ce code : il n’est affiché qu’une fois. Il permet de suivre la demande sur la page « Suivre
        une demande », sans compte.
      </p>
    </section>
  );
}

// ---------- Signalements ----------

export function ComplaintsPage() {
  const { me } = useAuth();
  const [rows, setRows] = useState<
    {
      id: string;
      reference: string;
      kind: string;
      message: string;
      status: string;
      response: string | null;
      created_at: string;
    }[]
  >([]);
  const [form, setForm] = useState({ kind: 'erreur_inscription', applicationReference: '', message: '' });
  const [response, setResponse] = useState<Record<string, string>>({});
  const [error, setError] = useState<ApiError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const agent = can(me, 'casier.controler');
  const load = () => api<typeof rows>('/signalements').then(setRows).catch(setError);
  useEffect(() => {
    void load();
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const r = await api<{ reference: string }>('/signalements', {
        method: 'POST',
        body: {
          kind: form.kind,
          message: form.message,
          ...(form.applicationReference ? { applicationReference: form.applicationReference } : {}),
        },
      });
      setNotice(`Signalement ${r.reference} enregistré.`);
      setForm({ ...form, message: '' });
      await load();
    } catch (err) {
      setError(err as ApiError);
    }
  };
  const handle = async (id: string, status: string) => {
    try {
      await api(`/signalements/${id}/traiter`, {
        method: 'POST',
        body: { status, response: response[id] ?? '' },
      });
      await load();
    } catch (err) {
      setError(err as ApiError);
    }
  };

  return (
    <div className="stack" style={{ maxWidth: 900 }}>
      <h1>Signalements</h1>
      <p className="muted">
        Un signalement ne modifie jamais directement une inscription : il est instruit selon la procédure de
        rectification prévue par la loi.
      </p>
      {!agent && (
        <form className="card stack" onSubmit={submit}>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="sg-kind">Nature</label>
              <select
                id="sg-kind"
                value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value })}
              >
                <option value="erreur_inscription">Erreur sur une inscription</option>
                <option value="erreur_identite">Erreur d’identité</option>
                <option value="delai">Délai</option>
                <option value="autre">Autre</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="sg-ref">Référence de demande (facultatif)</label>
              <input
                id="sg-ref"
                placeholder="CJ-…"
                value={form.applicationReference}
                onChange={(e) =>
                  setForm({ ...form, applicationReference: e.target.value.trim().toUpperCase() })
                }
              />
            </div>
          </div>
          <div className="field">
            <label htmlFor="sg-msg">Description</label>
            <textarea
              id="sg-msg"
              required
              minLength={10}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
            />
          </div>
          <div>
            <button className="btn" type="submit">
              Envoyer
            </button>
          </div>
        </form>
      )}
      <ErrorBox error={error} />
      {notice && <p className="alert alert-ok">{notice}</p>}
      <div className="card table-wrap" style={{ padding: 0 }}>
        <table>
          <thead>
            <tr>
              <th scope="col">Référence</th>
              <th scope="col">Message</th>
              <th scope="col">Statut et réponse</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={3} className="muted">
                  Aucun signalement.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.id}>
                <td>
                  <code>{r.reference}</code>
                  <div className="small muted">{formatDate(r.created_at)}</div>
                </td>
                <td className="small">{r.message}</td>
                <td className="small">
                  <Badge tone={r.status === 'close' ? 'muted' : 'info'}>{r.status}</Badge>
                  {r.response && <div>{r.response}</div>}
                  {agent && r.status !== 'close' && (
                    <div className="stack" style={{ marginTop: 6 }}>
                      <input
                        aria-label="Réponse"
                        value={response[r.id] ?? ''}
                        onChange={(e) => setResponse({ ...response, [r.id]: e.target.value })}
                      />
                      <div className="row">
                        <button
                          type="button"
                          className="btn btn-ghost"
                          onClick={() => void handle(r.id, 'orientee')}
                        >
                          Orienter
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost"
                          onClick={() => void handle(r.id, 'close')}
                        >
                          Clore
                        </button>
                      </div>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Suivi public ----------

export function TrackingPage() {
  const [form, setForm] = useState({ reference: '', code: '' });
  const [result, setResult] = useState<{ reference: string; statusLabel: string; updatedAt: string } | null>(
    null,
  );
  const [error, setError] = useState<ApiError | null>(null);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    try {
      setResult(
        await api('/suivi', {
          method: 'POST',
          body: { reference: form.reference.trim().toUpperCase(), code: form.code.trim().toUpperCase() },
        }),
      );
    } catch (err) {
      setError(err as ApiError);
    }
  };
  return (
    <div className="stack dm dm-track">
      <div className="head">
        <div>
          <h1>Suivre une demande</h1>
          <p className="lead">
            Saisissez la référence et le code de suivi figurant sur votre récépissé. Seul l’état de la demande
            est affiché.
          </p>
        </div>
      </div>
      <form className="card flow" onSubmit={submit}>
        <div className="fg">
          <div className="field">
            <label htmlFor="trk-ref">Référence</label>
            <input
              id="trk-ref"
              className="mono"
              required
              placeholder="CJ-XXXXXXXXXX"
              value={form.reference}
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
            />
          </div>
          <div className="field">
            <label htmlFor="trk-code">Code de suivi</label>
            <input
              id="trk-code"
              className="mono"
              required
              maxLength={8}
              autoComplete="off"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
            />
          </div>
        </div>
        <ErrorBox error={error} />
        <div className="actions">
          <button className="btn" type="submit">
            Consulter
          </button>
        </div>
      </form>
      {result && (
        <div className="alert alert-info" role="status">
          <span>
            Demande <span className="mono">{result.reference}</span> : <strong>{result.statusLabel}</strong>{' '}
            (mise à jour le {formatDate(result.updatedAt)}).
          </span>
        </div>
      )}
      <section className="card tint flow dm-account">
        <h2>Vous avez un compte ?</h2>
        <p className="muted">
          Connectez-vous pour voir le détail, payer les droits et télécharger le document.
        </p>
        <Link className="lnk" to="/connexion">
          Se connecter ›
        </Link>
      </section>
    </div>
  );
}

/** File des saisies hors ligne du poste de guichet et synchronisation (§18.3). */
function OfflineQueueCard() {
  const [items, setItems] = useState<{ commandId: string; label: string; createdAt: string }[]>([]);
  const [results, setResults] = useState<{ outcome: SyncOutcome; label: string }[]>([]);
  const [online, setOnline] = useState(typeof navigator === 'undefined' || navigator.onLine);
  const [error, setError] = useState<ApiError | null>(null);
  const load = () =>
    pending()
      .then((p) => setItems(p.map(({ commandId, label, createdAt }) => ({ commandId, label, createdAt }))))
      .catch(() => setItems([]));
  useEffect(() => {
    void load();
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  const run = async () => {
    setError(null);
    try {
      const { results: r, labels } = await synchronize();
      setResults(r.map((outcome) => ({ outcome, label: labels[outcome.commandId] ?? '' })));
      await load();
    } catch (e) {
      setError(e as ApiError);
    }
  };
  if (items.length === 0 && results.length === 0) return null;
  return (
    <section className="card flow">
      <div className="ch">
        <h2>Saisies hors ligne ({items.length})</h2>
        <button
          type="button"
          className="btn btn-gold"
          disabled={!online || items.length === 0}
          onClick={() => void run()}
        >
          {online ? 'Synchroniser' : 'Réseau indisponible'}
        </button>
      </div>
      <ErrorBox error={error} />
      {items.length > 0 && (
        <ul className="dm-docs">
          {items.map((i) => (
            <li key={i.commandId}>
              <span>
                {i.label || 'Brouillon'}
                <small className="dm-sub">saisi le {formatDate(i.createdAt)}</small>
              </span>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => void discard(i.commandId).then(load)}
              >
                Abandonner
              </button>
            </li>
          ))}
        </ul>
      )}
      {results.map(({ outcome, label }) => (
        <div
          key={outcome.commandId}
          className={`alert ${outcome.outcome === 'appliquee' ? 'alert-ok' : outcome.outcome === 'conflit' ? 'alert-warn' : 'alert-danger'} small`}
        >
          <strong>{label || 'Commande'}</strong> -{' '}
          {outcome.outcome === 'appliquee' ? (
            <>
              transmise : demande <code>{outcome.result?.reference}</code>
              {outcome.result?.trackingCode && (
                <>
                  , code de suivi <code>{outcome.result.trackingCode}</code> (à remettre à l’usager)
                </>
              )}
            </>
          ) : outcome.outcome === 'conflit' ? (
            <>conflit : {outcome.detail} La saisie est conservée pour examen.</>
          ) : (
            <>refusée : {outcome.detail}</>
          )}
        </div>
      ))}
    </section>
  );
}
